import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/auth";
import { GameProvider } from "@/lib/game";
import { DialogProvider } from "@/lib/dialog";
import { LightboxProvider } from "@/lib/lightbox";
import { CartProvider } from "@/lib/cart";
import { LegalModalProvider } from "@/components/LegalModal";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Toaster } from "sonner";
import { HitFullLoaderHost, AppBoot } from "@/components/loading";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import CardDetail from "@/pages/CardDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import SecurityItWasntMe from "@/pages/SecurityItWasntMe";
import ClaimAccount from "@/pages/ClaimAccount";
import CreateNewPasswordFromClaim from "@/pages/CreateNewPasswordFromClaim";
import Verify from "@/pages/Verify";
import FAQ from "@/pages/FAQ";
import Setup2FA from "@/pages/Setup2FA";
import PublishCard from "@/pages/PublishCard";
import PublishV2 from "@/pages/publish/PublishV2";

const PublishRedirect = () => { const l = useLocation(); return <Navigate to={`/publicar${l.search}`} replace />; };
import BuyerDashboard from "@/pages/BuyerDashboard";
import SellerDashboard from "@/pages/SellerDashboard";
import SellerProfile from "@/pages/SellerProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import Checkout from "@/pages/Checkout";
import Cart from "@/pages/Cart";
import MockPay from "@/pages/MockPay";
import PaymentResult from "@/pages/PaymentResult";
import OrderDetail from "@/pages/OrderDetail";
import Legal from "@/pages/Legal";
import Subastas from "@/pages/Subastas";
import SubastasCrear from "@/pages/SubastasCrear";
import SubastasPago from "@/pages/SubastasPago";
import SubastaPagar from "@/pages/SubastaPagar";
import MisSubastas from "@/pages/MisSubastas";
import SubastaDetalle from "@/pages/SubastaDetalle";
import SubastaCorta from "@/pages/SubastaCorta";
import Changelog from "@/pages/Changelog";
import Calificaciones from "@/pages/Calificaciones";
import RatingModal from "@/components/RatingModal";

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <AuthProvider>
        <GameProvider>
        <CartProvider>
          <DialogProvider>
            <LightboxProvider>
              <LegalModalProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <HitFullLoaderHost />
                  <AppBoot />
                  <RatingModal />
                  <Toaster
                    theme="dark"
                    position="top-right"
                    offset={80}
                    toastOptions={{ style: { background: "#171717", color: "#fff", border: "1px solid #262626" } }}
                  />
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/calificaciones" element={<ProtectedRoute><Calificaciones /></ProtectedRoute>} />
                      <Route path="/catalog" element={<Catalog />} />
                      <Route path="/card/:cardId/:language" element={<CardDetail />} />
                      <Route path="/card/:cardId/:language/:variant" element={<CardDetail />} />
                      <Route path="/carta/:slugId/:language" element={<CardDetail />} />
                      <Route path="/carta/:slugId/:language/:variant" element={<CardDetail />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/security/it-wasnt-me" element={<SecurityItWasntMe />} />
                      <Route path="/recuperar-cuenta" element={<ClaimAccount />} />
                      <Route path="/crear-nueva-contrasena" element={<CreateNewPasswordFromClaim />} />
                      <Route path="/legal/:slug" element={<Legal />} />
                      <Route path="/subastas" element={<Subastas />} />
                      <Route path="/subastas/mis-subastas" element={<ProtectedRoute><MisSubastas /></ProtectedRoute>} />
                      <Route path="/subastas/crear" element={<ProtectedRoute requireVerified><SubastasCrear /></ProtectedRoute>} />
                      <Route path="/subastas/pago/:draft_token" element={<ProtectedRoute requireVerified><SubastasPago /></ProtectedRoute>} />
                      <Route path="/subastas/pagar/:id" element={<ProtectedRoute><SubastaPagar /></ProtectedRoute>} />
                      <Route path="/subastas/:id" element={<SubastaDetalle />} />
                      <Route path="/s/:code" element={<SubastaCorta />} />
                      <Route path="/novedades" element={<ProtectedRoute requireAdmin><Changelog /></ProtectedRoute>} />
                      <Route path="/mock-pay" element={<MockPay />} />
                      <Route path="/payment-result" element={<PaymentResult />} />
                      <Route path="/verify" element={<ProtectedRoute><Verify /></ProtectedRoute>} />
                      <Route path="/setup-2fa" element={<ProtectedRoute><Setup2FA /></ProtectedRoute>} />
                      <Route path="/publish" element={<PublishRedirect />} />
                      <Route path="/publicar" element={<ProtectedRoute requireVerified><PublishV2 /></ProtectedRoute>} />
                      <Route path="/publicar-legacy" element={<ProtectedRoute requireAdmin requireVerified><PublishCard /></ProtectedRoute>} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                      <Route path="/buyer" element={<ProtectedRoute><BuyerDashboard /></ProtectedRoute>} />
                      <Route path="/seller" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />
                      <Route path="/vendedor/:sellerId" element={<SellerProfile />} />
                      <Route path="/tienda/:slug" element={<SellerProfile />} />
                      <Route path="/order/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
                      <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
                      <Route path="/admin/*" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
                    </Routes>
                  </Layout>
                </BrowserRouter>
              </LegalModalProvider>
            </LightboxProvider>
          </DialogProvider>
        </CartProvider>
        </GameProvider>
      </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
